import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import MenuBar from "../components/MenuBar";
import { canAccessSalesDataImport } from "../features/salesDataImport/access";
import SalesImportNav from "../features/salesDataImport/components/SalesImportNav";
import { SALES_IMPORT_BRANCH_OPTIONS, salesImportBranchDisplayName } from "../features/salesDataImport/config/salesImportBranchIds";
import {
  fetchBrandGroupBranchReport,
  flattenBrandGroupBranchForExport,
  flattenUnmappedDetailsForExport,
  pickAmount,
} from "../features/salesDataImport/services/brandGroupBranchReportService";
import { formatFirebaseError } from "../features/salesDataImport/utils/firebaseError";
import "./SalesDataImportCenter.css";

const BRANCH_STORAGE_KEYS = {
  brand: "sdic-brand-by-branch-branches",
  group: "sdic-group-by-branch-branches",
};

const VOUCHER_STORAGE_KEYS = {
  brand: "sdic-brand-by-branch-voucher",
  group: "sdic-group-by-branch-voucher",
};

function readStoredVoucherType(storageKey) {
  try {
    return localStorage.getItem(storageKey) || "all";
  } catch {
    return "all";
  }
}

function readSelectedBranches(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [...SALES_IMPORT_BRANCH_OPTIONS];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return [...SALES_IMPORT_BRANCH_OPTIONS];
    return parsed.filter((id) => SALES_IMPORT_BRANCH_OPTIONS.includes(id));
  } catch {
    return [...SALES_IMPORT_BRANCH_OPTIONS];
  }
}

function writeSelectedBranches(storageKey, ids) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

function fm(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export default function BrandGroupBranchReportPage({ dimension = "brand" }) {
  const isGroup = dimension === "group";
  const dimensionLabel = isGroup ? "Group" : "Brand";
  const navActive = isGroup ? "group-by-branch" : "brand-by-branch";
  const branchStorageKey = BRANCH_STORAGE_KEYS[dimension] || BRANCH_STORAGE_KEYS.brand;
  const voucherStorageKey = VOUCHER_STORAGE_KEYS[dimension] || VOUCHER_STORAGE_KEYS.brand;
  const masterLink = isGroup ? "/group-master" : "/brand-master";
  const masterLabel = isGroup ? "Group Master" : "Brand Master";
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const nowYear = new Date().getFullYear();
  const [dark, setDark] = useState(() => localStorage.getItem("sdic-dark") === "1");
  const [year, setYear] = useState(nowYear);
  const [amountType, setAmountType] = useState("sales");
  const [voucherType, setVoucherType] = useState(() => readStoredVoucherType(voucherStorageKey));
  const [voucherOptions, setVoucherOptions] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState(() => readSelectedBranches(branchStorageKey));
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [report, setReport] = useState(null);
  const [search, setSearch] = useState("");
  const [showUnmappedDetails, setShowUnmappedDetails] = useState(true);
  const [expandedUnmapped, setExpandedUnmapped] = useState({});

  const yearOptions = useMemo(() => [nowYear - 1, nowYear, nowYear + 1], [nowYear]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return report?.rows || [];
    return (report?.rows || []).filter((r) => String(r.label || "").toLowerCase().includes(term));
  }, [report, search]);

  const amountHeading =
    amountType === "purchase" ? "Purchase" : amountType === "net" ? "Net" : "Sales";

  const toggleBranch = (branchId) => {
    setSelectedBranches((prev) => {
      const next = prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId];
      writeSelectedBranches(branchStorageKey, next.length ? next : [...SALES_IMPORT_BRANCH_OPTIONS]);
      return next.length ? next : [...SALES_IMPORT_BRANCH_OPTIONS];
    });
  };

  const loadReport = useCallback(async () => {
    if (!selectedBranches.length) {
      toast.error("Select at least one branch.");
      return;
    }
    setLoading(true);
    setProgress(0);
    try {
      const out = await fetchBrandGroupBranchReport(
        {
          year,
          branchIds: selectedBranches,
          dimension,
          amountType,
          voucherType,
          combineOeIntoBase: false,
        },
        (n) => setProgress(n)
      );
      setReport(out);
      setVoucherOptions(out.voucherTypeOptions || []);
      if (!out.rows.length) {
        toast.error(`No ${dimensionLabel.toLowerCase()} master entries or matching lines for this year.`);
      } else {
        toast.success(
          `${out.masterCount || 0} ${dimensionLabel.toLowerCase()}(s) from master · ${out.lineCount.toLocaleString()} matched lines`
        );
      }
    } catch (e) {
      toast.error(formatFirebaseError(e));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [year, selectedBranches, dimension, amountType, voucherType, dimensionLabel]);

  useEffect(() => {
    if (!canAccessSalesDataImport(user)) return;
    loadReport();
  }, [user, loadReport]);

  useEffect(() => {
    try {
      localStorage.setItem(voucherStorageKey, voucherType || "all");
    } catch {
      /* ignore */
    }
  }, [voucherType, voucherStorageKey]);

  useEffect(() => {
    localStorage.setItem("sdic-dark", dark ? "1" : "0");
  }, [dark]);

  useEffect(() => {
    if (
      voucherType !== "all" &&
      voucherOptions.length > 0 &&
      !voucherOptions.includes(voucherType)
    ) {
      setVoucherType("all");
    }
  }, [voucherOptions, voucherType]);

  useEffect(() => {
    writeSelectedBranches(branchStorageKey, selectedBranches);
  }, [selectedBranches, branchStorageKey]);

  const exportExcel = () => {
    if (!report?.rows?.length) {
      toast.error("Nothing to export.");
      return;
    }
    const rows = flattenBrandGroupBranchForExport(report);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isGroup ? "ByGroup" : "ByBrand");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `${dimension}-by-branch-${year}${voucherType !== "all" ? `-${voucherType.replace(/\s+/g, "-")}` : ""}.xlsx`
    );
  };

  const exportUnmappedExcel = () => {
    if (!report?.unmappedDetails?.length) {
      toast.error("No unmapped details to export.");
      return;
    }
    const { summaryRows, sampleRows } = flattenUnmappedDetailsForExport(report);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), "UnmappedSummary");
    if (sampleRows.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sampleRows), "SampleLines");
    }
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `${dimension}-unmapped-details-${year}.xlsx`
    );
  };

  const toggleUnmappedExpanded = (label) => {
    setExpandedUnmapped((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  if (!canAccessSalesDataImport(user)) {
    return <div className="p-6 text-center">Access denied — Admin or Storekeeper only.</div>;
  }

  return (
    <div className={`sdic-root${dark ? " sdic-dark" : ""}`}>
      <MenuBar />
      <header className="sdic-header">
        <div>
          <h1>{dimensionLabel} by Branch</h1>
          <p>
            {amountHeading} amounts for each <strong>{dimensionLabel.toLowerCase()}</strong> from{" "}
            <Link to={masterLink}>{masterLabel}</Link>
            {voucherType !== "all" ? (
              <>
                {" "}
                · voucher: <strong>{voucherType}</strong>
              </>
            ) : null}
            {" "}
            · matched to import lines for selected branches
          </p>
        </div>
        <button type="button" className="sdic-btn sdic-btn-secondary" onClick={() => setDark((d) => !d)}>
          {dark ? "Light" : "Dark"}
        </button>
      </header>

      <div className="sdic-body">
        <SalesImportNav active={navActive} showBranchFilter={false} />

        <section className="sdic-panel">
          <div className="sdic-week-banner" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
            <label>
              Year
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                style={{ padding: "0.4rem", minWidth: "100px" }}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Voucher type
              <select
                value={voucherType}
                onChange={(e) => setVoucherType(e.target.value)}
                style={{ padding: "0.4rem", minWidth: "180px" }}
              >
                <option value="all">All voucher types</option>
                {voucherOptions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Amount
              <select
                value={amountType}
                onChange={(e) => setAmountType(e.target.value)}
                style={{ padding: "0.4rem", minWidth: "120px" }}
              >
                <option value="sales">Sales</option>
                <option value="purchase">Purchase</option>
                <option value="net">Net (Sales − Purchase)</option>
              </select>
            </label>
            <button
              type="button"
              className="sdic-btn sdic-btn-primary"
              disabled={loading}
              onClick={loadReport}
            >
              {loading ? `Loading… ${progress.toLocaleString()} lines` : "Refresh"}
            </button>
            <button
              type="button"
              className="sdic-btn sdic-btn-secondary"
              disabled={!report?.rows?.length}
              onClick={exportExcel}
            >
              Export Excel
            </button>
            <input
              className="sdic-search"
              style={{ margin: 0, maxWidth: 220 }}
              placeholder={`Search ${dimensionLabel.toLowerCase()}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div style={{ marginTop: "0.75rem" }}>
            <div style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.35rem" }}>
              Branches
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem 1.25rem" }}>
              {SALES_IMPORT_BRANCH_OPTIONS.map((id) => (
                <label key={id} style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.85rem" }}>
                  <input
                    type="checkbox"
                    checked={selectedBranches.includes(id)}
                    onChange={() => toggleBranch(id)}
                  />
                  {salesImportBranchDisplayName(id)}
                </label>
              ))}
              <button
                type="button"
                className="sdic-btn sdic-btn-secondary"
                style={{ padding: "0.2rem 0.6rem", fontSize: "0.78rem" }}
                onClick={() => setSelectedBranches([...SALES_IMPORT_BRANCH_OPTIONS])}
              >
                All
              </button>
            </div>
          </div>

          <p style={{ fontSize: "0.83rem", color: "var(--sdic-muted)", margin: "0.75rem 0 0" }}>
            Sales = all voucher types except <strong>Inward Supply</strong>. Purchase ={" "}
            <strong>Inward Supply</strong> only. Uses line <strong>Amount</strong> and{" "}
            <strong>Trans Date</strong>.
            {!isGroup && (
              <>
                {" "}
                When <strong>BrandName</strong> was not uploaded, brand is resolved from{" "}
                <strong>Part No → Products master</strong>, then <strong>Item Group</strong>, Alias,
                and Item Name.
              </>
            )}
          </p>
        </section>

        {!isGroup && report?.brandNameMissing && (
          <section
            className="sdic-panel"
            style={{ borderColor: "var(--sdic-accent)", marginBottom: "0.75rem" }}
          >
            <strong>BrandName column not in upload</strong>
            <p style={{ fontSize: "0.85rem", margin: "0.35rem 0 0", color: "var(--sdic-muted)" }}>
              {(report.rowsWithoutBrandName || 0).toLocaleString()} of{" "}
              {((report.rowsWithoutBrandName || 0) + (report.rowsWithBrandName || 0)).toLocaleString()}{" "}
              lines have no BrandName. Brands are taken from{" "}
              <Link to="/add-product">Products</Link> (Part No / Alias / Product ID → brand field), then
              Item Group text matched to <Link to={masterLink}>{masterLabel}</Link>.
              {(report.rowsResolvedFromItemsMaster || 0) > 0 && (
                <>
                  {" "}
                  · {(report.rowsResolvedFromItemsMaster || 0).toLocaleString()} line(s) matched via
                  Products master.
                </>
              )}
            </p>
          </section>
        )}

        {report && (
          <>
            <section className="sdic-panel">
              <h2 className="sdic-panel-title">
                Year {year} · {dimensionLabel}s · {amountHeading}
                {voucherType !== "all" ? ` · ${voucherType}` : ""}
              </h2>
              <div className="sdic-validation-grid">
                <div className="sdic-val-box sdic-val-valid">
                  Total sales
                  <br />₹ {fm(report.grandSales)}
                </div>
                <div className="sdic-val-box" style={{ borderColor: "var(--sdic-accent)" }}>
                  Total purchase
                  <br />₹ {fm(report.grandPurchase)}
                </div>
                <div className="sdic-val-box sdic-val-dup">
                  Net
                  <br />₹ {fm(report.grandNet)}
                </div>
                <div className="sdic-val-box">
                  Master {dimensionLabel.toLowerCase()}s
                  <br />
                  {report.masterCount ?? report.rows.filter((r) => !r.isUnmapped).length}
                </div>
                <div className="sdic-val-box sdic-val-dup">
                  Unmapped lines
                  <br />
                  {(report.unmappedLineCount || 0).toLocaleString()}
                </div>
              </div>
            </section>

            <section className="sdic-panel">
              <h2 className="sdic-panel-title">
                {dimensionLabel} × branch amounts
              </h2>
              <div className="sdic-table-wrap" style={{ maxHeight: "70vh" }}>
                <table className="sdic-table">
                  <thead>
                    <tr>
                      <th style={{ position: "sticky", left: 0, zIndex: 2, background: "inherit" }}>
                        {dimensionLabel}
                      </th>
                      {(report.branches || []).map((br) => (
                        <th key={br.id} style={{ textAlign: "right" }}>
                          {br.label}
                        </th>
                      ))}
                      <th style={{ textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr key={row.key} style={row.isUnmapped ? { fontStyle: "italic", opacity: 0.85 } : undefined}>
                        <td style={{ position: "sticky", left: 0, fontWeight: 600, background: "inherit" }}>
                          {row.isUnmapped ? (
                            <button
                              type="button"
                              className="sdic-btn sdic-btn-secondary"
                              style={{
                                padding: "0.1rem 0.35rem",
                                fontSize: "inherit",
                                fontStyle: "italic",
                              }}
                              onClick={() => {
                                setShowUnmappedDetails(true);
                                document.getElementById("unmapped-details-panel")?.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              }}
                            >
                              {row.label} ↓
                            </button>
                          ) : (
                            row.label
                          )}
                        </td>
                        {(report.branches || []).map((br) => {
                          const cell = row.byBranch[br.id] || { salesAmount: 0, purchaseAmount: 0 };
                          return (
                            <td key={`${row.key}-${br.id}`} style={{ textAlign: "right" }}>
                              ₹ {fm(pickAmount(cell, amountType))}
                            </td>
                          );
                        })}
                        <td style={{ textAlign: "right" }}>
                          <strong>
                            ₹{" "}
                            {fm(
                              pickAmount(
                                {
                                  salesAmount: row.totalSales,
                                  purchaseAmount: row.totalPurchase,
                                },
                                amountType
                              )
                            )}
                          </strong>
                        </td>
                      </tr>
                    ))}
                    {!filteredRows.length && (
                      <tr>
                        <td colSpan={(report.branches?.length || 0) + 2} style={{ color: "var(--sdic-muted)" }}>
                          No rows match your search.
                        </td>
                      </tr>
                    )}
                    {!!filteredRows.length && (
                      <tr style={{ fontWeight: 700, background: "var(--sdic-bg-soft, rgba(0,0,0,0.04))" }}>
                        <td style={{ position: "sticky", left: 0, background: "inherit" }}>
                          Total
                        </td>
                        {(report.branches || []).map((br) => {
                          const t = report.branchTotals?.[br.id] || { salesAmount: 0, purchaseAmount: 0 };
                          return (
                            <td key={`tot-${br.id}`} style={{ textAlign: "right" }}>
                              ₹ {fm(pickAmount(t, amountType))}
                            </td>
                          );
                        })}
                        <td style={{ textAlign: "right" }}>
                          ₹{" "}
                          {fm(
                            pickAmount(
                              { salesAmount: report.grandSales, purchaseAmount: report.grandPurchase },
                              amountType
                            )
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {!!report.unmappedDetails?.length && (
              <section className="sdic-panel" id="unmapped-details-panel">
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.75rem",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <h2 className="sdic-panel-title" style={{ marginBottom: "0.35rem" }}>
                      Unmapped details — not in {masterLabel}
                    </h2>
                    <p style={{ fontSize: "0.83rem", color: "var(--sdic-muted)", margin: 0 }}>
                      Import text that could not be matched to any {dimensionLabel.toLowerCase()} in{" "}
                      <Link to={masterLink}>{masterLabel}</Link>. Add missing names there, or fix import
                      column values below.
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="sdic-btn sdic-btn-secondary"
                      onClick={() => setShowUnmappedDetails((v) => !v)}
                    >
                      {showUnmappedDetails ? "Hide" : "Show"} details
                    </button>
                    <button
                      type="button"
                      className="sdic-btn sdic-btn-secondary"
                      onClick={exportUnmappedExcel}
                    >
                      Export unmapped Excel
                    </button>
                  </div>
                </div>

                {showUnmappedDetails && (
                  <div className="sdic-table-wrap" style={{ maxHeight: "70vh", marginTop: "0.75rem" }}>
                    <table className="sdic-table">
                      <thead>
                        <tr>
                          <th style={{ width: 36 }} />
                          <th>Import text</th>
                          <th>Source fields on line</th>
                          <th>Lines</th>
                          <th>Voucher types</th>
                          {(report.branches || []).map((br) => (
                            <th key={`um-${br.id}`} style={{ textAlign: "right" }}>
                              {br.label}
                            </th>
                          ))}
                          <th style={{ textAlign: "right" }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.unmappedDetails.map((detail) => (
                          <React.Fragment key={detail.label}>
                            <tr>
                              <td>
                                {!!detail.samples?.length && (
                                  <button
                                    type="button"
                                    className="sdic-btn sdic-btn-secondary"
                                    style={{ padding: "0.1rem 0.35rem", fontSize: "0.75rem" }}
                                    onClick={() => toggleUnmappedExpanded(detail.label)}
                                  >
                                    {expandedUnmapped[detail.label] ? "−" : "+"}
                                  </button>
                                )}
                              </td>
                              <td style={{ fontWeight: 600 }}>{detail.label}</td>
                              <td style={{ fontSize: "0.82rem", maxWidth: 320 }}>{detail.source}</td>
                              <td>{detail.lineCount.toLocaleString()}</td>
                              <td style={{ fontSize: "0.82rem" }}>{detail.voucherSummary || "—"}</td>
                              {(report.branches || []).map((br) => {
                                const cell = detail.byBranch?.[br.id] || {
                                  salesAmount: 0,
                                  purchaseAmount: 0,
                                };
                                return (
                                  <td key={`${detail.label}-${br.id}`} style={{ textAlign: "right" }}>
                                    ₹ {fm(pickAmount(cell, amountType))}
                                  </td>
                                );
                              })}
                              <td style={{ textAlign: "right" }}>
                                <strong>
                                  ₹{" "}
                                  {fm(
                                    pickAmount(
                                      {
                                        salesAmount: detail.totalSales,
                                        purchaseAmount: detail.totalPurchase,
                                      },
                                      amountType
                                    )
                                  )}
                                </strong>
                              </td>
                            </tr>
                            {expandedUnmapped[detail.label] && !!detail.samples?.length && (
                              <tr>
                                <td colSpan={(report.branches?.length || 0) + 6}>
                                  <div
                                    style={{
                                      padding: "0.5rem 0.75rem",
                                      background: "var(--sdic-bg-soft, rgba(0,0,0,0.04))",
                                      borderRadius: 6,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "0.8rem",
                                        fontWeight: 600,
                                        marginBottom: "0.35rem",
                                      }}
                                    >
                                      Sample lines (up to 10)
                                    </div>
                                    <table className="sdic-table" style={{ fontSize: "0.8rem" }}>
                                      <thead>
                                        <tr>
                                          <th>Tran Date</th>
                                          <th>Voucher Type</th>
                                          <th>Voucher No</th>
                                          <th>Part No</th>
                                          <th>BrandName</th>
                                          <th>Item Group</th>
                                          <th style={{ textAlign: "right" }}>Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {detail.samples.map((s, idx) => (
                                          <tr key={`${detail.label}-sample-${idx}`}>
                                            <td>{s.tranDate || "—"}</td>
                                            <td>{s.voucherType || "—"}</td>
                                            <td>{s.voucherNo || "—"}</td>
                                            <td>{s.partNo || "—"}</td>
                                            <td>{s.brandName || "—"}</td>
                                            <td>{s.itemGroup || "—"}</td>
                                            <td style={{ textAlign: "right" }}>₹ {fm(s.amount)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </>
        )}

        {!loading && !report?.rows?.length && (
          <p style={{ color: "var(--sdic-muted)" }}>
            No data for {year}. Add {dimensionLabel.toLowerCase()}s in{" "}
            <Link to={masterLink}>{masterLabel}</Link> and upload import lines with{" "}
            {isGroup ? "Item Group" : "BrandName"}, BranchID, and Trans Date in{" "}
            <Link to="/sales-data-import">Sales Data Import Center</Link>.
          </p>
        )}
      </div>
    </div>
  );
}
